import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'hooks/config/queryClient';
import { getEvent } from 'services/Calender/calendarServices';

/**
 * Custom hook to fetch a single event OR task by ID
 * The same API endpoint returns both events and tasks
 * Differentiated by res_type field: "event" or "task"
 *
 * @param {string} itemId - The event/task ID to fetch
 * @param {object} itemMetadata - Additional metadata (for recurring events)
 * @param {boolean} enabled - Whether to enable the query
 * @returns {object} Query result with event/task data
 */
export const useSingleEvent = (itemId, itemMetadata = null, enabled = true) => {
  return useQuery({
    queryKey: [...queryKeys.calendarEvents.detail(itemId || itemMetadata?.sk)],
    queryFn: async () => {
      if (!itemId) {
        throw new Error('Item ID is required');
      }

      const response = await getEvent(itemId, { extendedProps: { sk: itemId?.includes('#') ? itemId : 'event#' + itemId } }, true);
      const notLinkedToCase = !Boolean(response.data.case_id);
      return { ...(response?.data || {}), not_linked_to_case: notLinkedToCase };
    },
    enabled: enabled && !!itemId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
};
